/**
 * PdwStage4DocsGrid — Personal-cohort docs-to-capture surface.
 *
 * Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6): the
 * separate PdwShell 'docs-grid' STAGE is gone — this grid is now hosted
 * INSIDE the consolidated Individual body (IndividualReviewBody, Documents
 * section) and re-derives live from the draft's Citizenship selection. Owns
 * the DocsToCaptureGrid (tile presentation + drop targets) + PerDocScanModal
 * (per-tile capture) + the CIP slot machine. Captured docs auto-populate the
 * downstream sections of the same body.
 *
 * v0.14.50 — BACKLOG-0072 (Bill 2026-05-26). First batch of the Stage 4 wire-
 * up. End-to-end functional for both NRA and US-Citizen / PR cohorts:
 *
 *   - NRA cohorts (passport / visa / i94 / branch-doc): scan dispatches to
 *     `apiClient.retail.nra.scan` (existing endpoint backed by Azure DI multi-doc
 *     extraction). Successful scans patch the seeded RaoPerson draft via the
 *     same `mapToPersonPatch` shape used by IndividualNraDocumentScanTabs —
 *     downstream Stage 9 tabs see the auto-filled values immediately.
 *   - US Citizen / PR cohorts (primary ID / green card): scan dispatches to
 *     the same `v3.nra.scan` endpoint (server-side classifier handles
 *     passport + DL extraction); for slot codes the classifier doesn't
 *     recognize, the response success=true with empty fields still marks
 *     the tile captured — the raw file attaches via the registered
 *     AttachedDoc, and OCR auto-fill for those slots layers on in a later
 *     batch (BACKLOG-0073+).
 *
 * The per-doc scan-loop ergonomics — auto-advance to next required tile,
 * sub-stage transitions inside PerDocScanModal, audit events per SPEC
 * § 7.4.2 — are BACKLOG-0073 deliverables.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfirm } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useOptionalCart } from '@/contexts/CartContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { RaoPerson } from '../../types';
import { DocsToCaptureGrid, type DocTileSpec, type ExtractedFieldRow } from './DocsToCaptureGrid';
import { PerDocScanModal, type ScanResultPayload, type CaptureArtifact } from './PerDocScanModal';
import {
  cohortDocTiles,
  deriveCohortKey,
  type CohortDocTileSpec,
} from './cohortDocTiles';
import { makeScanHandler, makeAutoClassify } from './capture/scanRouter';
import { type CipIdPriorityRule } from './capture/assignCipSlots';
import {
  azureDiTypeToDocTypeValue,
  classifyPickerOptions,
  docTypeBranding,
  extractsForDocType,
  extractsKeyForDocType,
  findDocType,
  tileIdToCanonicalDocCode,
  type DocTypeBranding,
} from './individual/captureRouting';
import { buildDisplayRows, correctionsToPersonPatch } from './capture/extractDisplay';
import {
  CIP_CORRECTION_KEYS,
  extractWithCorrections,
  planCipSlots,
  type CipFamily,
  type OnFileId,
  type SlotCapture,
} from './capture/cipSlotPlan';
import {
  derivePrimaryIdGating,
  readExistingIdExpiringSoonGate,
  readIdExpirationWarningDays,
  readAcceptExpiredGraceMonths,
} from './shared/existingCustomer';
import { type AttachedDoc, estimateBase64Size, readAttachedDocs } from './shared/attachedDocs';
import { pdfFirstPageThumbnailFromBase64 } from '../../utils/pdfThumbnail';

/**
 * Step 2/5 — CapturedSlotInfo holds the classified branding + F254 code
 * for a tile (generic Primary/Secondary or otherwise) after a successful
 * scan. Drives the tile's adopt-on-classify display + the CIP slot-machine
 * reassignment in computeCipSlotAssignments.
 */
interface CapturedSlotInfo {
  docTypeValue: string;
  branding: DocTypeBranding;
}

/**
 * Address Verification RELOCATED (Bill 2026-06-11, LOCKED mockup v6): the
 * 'address-proof' tile no longer renders in this grid — it lives beside the
 * Physical/Mailing fields it corroborates (AddressVerificationTile inside the
 * Address & Contact section), driven by the reactive "corroborated yet?" rule
 * (individual/addressCorroboration.ts). The old Step-6/Step-7
 * OPTIONAL→REQUIRED mismatch flip that lived here is retired with it; the
 * admin toggle `cip.addressVerification.requireOnMismatch` now has exactly
 * one consumer — the relocated tile's REQUIRED escalation.
 */
const RELOCATED_TILE_IDS: ReadonlySet<string> = new Set(['address-proof']);

/**
 * Step 5 — CIP slot-machine. Given the set of captured tiles + their
 * detected doc types (with F254 codes), assigns Primary / Secondary ID
 * slot labels by ASCENDING F254 priority (1 DL > 2 State ID > 3 Passport >
 * 4 Military > 5 SSN > 6 State Docs > 7 Birth Cert > 8 Other). The
 * generic 'primary-id' / 'secondary-id' tiles are the slot containers;
 * specific tiles (green-card / passport / etc.) keep their own identity
 * but still feed into the ranking so the F254-best photo ID always lands
 * in the Primary slot.
 *
 * Returns a map of tileId → assigned slot ('primary' | 'secondary' | null).
 * NEVER silent-drops: a third+ ID with a F254 code retains its specific-
 * tile slot (e.g. green-card keeps tileId='green-card' but no primary/
 * secondary label) and remains on the cart for audit.
 *
 * Bill 2026-05-27 reference_cd_idverifycode_field254_verified_20260527.
 */

/**
 * CIP-commit Step 2 (Bill 2026-06-01) — resolve the per-doc extract object for
 * a slot's docTypeValue.
 *
 * BACKLOG-0600 — both capture paths now write `person.extra.extracts` under
 * the CANONICAL doc-type value, and `extractsForDocType` (captureRouting)
 * tolerates every legacy key: the v3-nra wire id ('passport', 'ssn') and the
 * generic-slot route value ('primary-id' / 'secondary-id' — passed here as
 * `legacyTileId`) so persons captured before the canonical-key fix still
 * resolve their CIP family.
 */
function cipExtractsFor(
  extractsRoot: Record<string, unknown>,
  docTypeValue: string,
  legacyTileId?: string,
): Record<string, { value?: unknown }> {
  return extractsForDocType(extractsRoot, docTypeValue, legacyTileId ? [legacyTileId] : []);
}

/**
 * Pull the canonical CIP family from a per-doc extract object, tolerant of the
 * divergent field-key conventions across capture paths (azure idNumber/idIssuer/…
 * · foreign-passport foreignPassport{Number,Country,IssueDt,ExpDt} · green-card
 * greenCard{ANumber,ResidentSince,ExpDt}). Mirrors DocumentTileGrid.toConflictDoc's
 * multi-key g(...) getter. Empty string for any field the doc didn't carry.
 */
function pullCipFamily(ex: Record<string, { value?: unknown }>): CipFamily {
  const g = (...keys: string[]): string => {
    for (const k of keys) {
      const v = ex[k]?.value;
      if (v != null && v !== '') return String(v);
    }
    return '';
  };
  return {
    // ssnNumber/eadANumber added (Bill 2026-06-02): an SS Card extract carries
    // its number under `ssnNumber` (EAD under `eadANumber`), not `idNumber`, so
    // when one fills the 2nd-ID slot its number was dropped — Field 801 came up
    // blank with the type correctly set to "5 SSN Card". These fallbacks route
    // the doc's identifying number into the CIP slot.
    idNumber:     g('idNumber', 'foreignPassportNumber', 'greenCardANumber', 'eadANumber', 'ssnNumber', 'documentNumber'),
    idIssuer:     g('idIssuer', 'foreignPassportCountry', 'issuingAuthority'),
    idIssueDate:  g('idIssueDate', 'foreignPassportIssueDt', 'greenCardResidentSince', 'issueDate'),
    idExpiryDate: g('idExpiryDate', 'foreignPassportExpDt', 'greenCardExpDt', 'expirationDate'),
  };
}


export interface PdwStage4DocsGridProps {
  /** Seeded Personal draft (cohort already chosen in Stage 2). */
  person: RaoPerson;
  /** Patch handler — applied on every successful scan-apply. */
  onChange: (patch: Partial<RaoPerson>) => void;
  /**
   * Fired when the set of captured tile ids changes. PdwShell reads it to
   * gate the stage-level Continue button (advance to Stage 9 only when all
   * required tiles captured for the cohort).
   */
  onCaptureStateChange: (state: { capturedTileIds: ReadonlySet<string>; allRequiredCaptured: boolean }) => void;
  /**
   * Contract C2 — fired on every successful Apply with the captured doc built
   * from the merged-PDF artifact. PdwShell persists it onto
   * `personalDraft.extra.attachedDocs` so the capture survives the hop to Stage
   * 9 review (the Identity-Verified badge counts it; it reaches peopleJson on
   * save). Previously the grid tracked only `capturedTileIds` and the
   * AttachedDoc never reached the draft.
   */
  onAttachedDocsChange?: (docs: ReadonlyArray<AttachedDoc>) => void;
  /**
   * Person-Modal consolidation (Bill 2026-06-11) — the grid now also renders
   * inside the consolidated Individual body for existing-CIF edit / view
   * surfaces. When readOnly, tiles render their captured state (and the
   * quick-preview drawer still opens) but capture/re-scan/delete/drop are
   * disabled.
   */
  readOnly?: boolean;
}

/**
 * Stage 4 grid + scan modal wrapper. Returns just the grid body (no shell
 * frame); PdwShell owns the modal frame + footer dyad.
 */
export function PdwStage4DocsGrid({
  person,
  onChange,
  onCaptureStateChange,
  onAttachedDocsChange,
  readOnly = false,
}: PdwStage4DocsGridProps): JSX.Element {
  const apiClient = useApiClient();
  const { config } = useConfig();
  // BACKLOG-1297 — in-app remove confirm (Teams no-ops window.confirm).
  const confirm = useConfirm();
  const cohort = useMemo(() => deriveCohortKey(person), [person]);
  // Filter out tiles relocated off this grid (address-proof → Address &
  // Contact section). Belt-and-suspenders: the tiles lane removed
  // 'address-proof' from cohortDocTiles' shared optional set; this filter
  // keeps the grid correct against either version of the tile spec.
  // Existing-customer Primary Photo ID gating (Bill 2026-07-08, in-branch):
  // an existing CIF with a valid ID on file doesn't RE-require a photo ID —
  // CIP-expiration is the standing gate. expired/missing → required;
  // expiring-soon → admin-configurable (hard-stop | recommend, default
  // recommend); valid → satisfied (downgraded to OPTIONAL so it doesn't block
  // Continue). A fresh add is unchanged (always required).
  const primaryIdGating = useMemo(
    () => derivePrimaryIdGating(
      person,
      readIdExpirationWarningDays(config.systemSettings),
      readExistingIdExpiringSoonGate(config.systemSettings),
      // C2: an ID expired within the admin grace window (default 6mo, Collette)
      // downgrades from 'required' hard-block to 'recommended' (OPTIONAL tile).
      readAcceptExpiredGraceMonths(config.systemSettings),
    ),
    [person, config.systemSettings],
  );

  const baseTileSpecs = useMemo(
    () => cohortDocTiles(cohort)
      .filter((t) => !RELOCATED_TILE_IDS.has(t.tileId))
      .map((t) => {
        // Only the primary CIP photo-ID tile is subject to the existing-CIF
        // downgrade — NRA lawful-presence docs keep their own rules.
        if (t.tileId !== 'primary-id' || primaryIdGating === 'required') return t;
        return {
          ...t,
          gatingLevel: 'OPTIONAL' as const,
          hint: primaryIdGating === 'satisfied'
            ? 'On file at Core Director (unexpired) — capture only to update it.'
            // 'recommended' now covers both expiring-soon AND expired-within-grace (C2).
            : 'Update recommended — ID expired or expiring; capture a current ID.',
        };
      }),
    [cohort, primaryIdGating],
  );

  // CIP slot-machine priority (Bill 2026-06-01) — map the global config rows to
  // the assignCipSlots rule shape so capture-time slotting honors admin priority
  // + the stable tiebreak (same source as IndividualCipScanSection). Empty until
  // /v3/config/cip-id-priority loads; assignCipSlots then falls back to F254 order.
  const cipIdPriorityRules = useMemo<ReadonlyArray<CipIdPriorityRule>>(
    () =>
      (config.cipIdPriority ?? [])
        .filter((r) => r.isActive)
        .map((r) => ({
          customerType: '*',
          idTypeCode: r.idTypeCode,
          rank: r.rank ?? Number.MAX_SAFE_INTEGER,
          isActive: r.isActive,
        })),
    [config.cipIdPriority],
  );
  const [capturedTileIds, setCapturedTileIds] = useState<ReadonlySet<string>>(new Set());
  // C2 — running AttachedDoc set keyed by tileId (docCode). Emitted to PdwShell
  // on every capture/delete so the captured docs persist onto the draft.
  const [attachedByTileId, setAttachedByTileId] = useState<Record<string, AttachedDoc>>({});
  const [scanTile, setScanTile] = useState<CohortDocTileSpec | null>(null);
  // slot-1 G4/T6 — file dropped on a tile. When set, PerDocScanModal opens with
  // it as `initialFile` and auto-starts extraction (no start gate). Cleared on
  // apply / close so a click-opened or auto-advanced tile shows the chooser.
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  // BACKLOG-0074 (v0.14.52) — Stage 8 quick-preview drawer. State tracks
  // which captured tile (if any) currently has its in-place drawer open.
  // Set on captured-tile click; cleared on Close / outside-click / Esc /
  // Re-scan launch / Delete. Per SPEC Screen 16 the drawer is INLINE
  // (not a stacked modal) so docs-grid context stays visible.
  const [previewTileId, setPreviewTileId] = useState<string | null>(null);
  // Review import (Bill 2026-10-06): the last applied capture per tile (in-session only) and the
  // tile whose review is reopened. A review applies corrections and stays on that tile.
  const [lastScans, setLastScans] = useState<Record<string, { result: ScanResultPayload; artifact: CaptureArtifact; chosenDocType?: string }>>({});
  const [reviewTileId, setReviewTileId] = useState<string | null>(null);
  // Per-tile extracted-field cache. Populated on Apply so the drawer can
  // surface the field set without re-fetching. Keyed by tileId; cleared
  // on Delete. Stored as readonly snapshots so DocsToCaptureGrid renders
  // them directly.
  const [extractedByTileId, setExtractedByTileId] = useState<Record<string, ReadonlyArray<ExtractedFieldRow>>>({});
  // Step 2/5 — per-tile classified-doc info (label + icon + F254 code).
  // GENERIC tiles (primary-id / secondary-id) populate this on capture so
  // DocsToCaptureGrid renders the adopted branding. Specific tiles
  // (passport, green-card, etc.) also record their own F254 here so the
  // slot-machine ranks them. Cleared on Delete.
  const [capturedSlots, setCapturedSlots] = useState<Record<string, CapturedSlotInfo>>({});
  // GAP E (Bill 2026-05-28) — per-tile rendered thumbnail. Drawer's
  // "THUMBNAIL UNAVAILABLE" placeholder was showing because AttachedDoc's
  // imageDataUrl is '' for PDF artifacts. This state caches the first-page
  // JPEG (rendered via pdf.js) per tile so the drawer's <img> renders. Set
  // by handleApply (single + stack paths); cleared on Delete.
  const [thumbnailByTileId, setThumbnailByTileId] = useState<Record<string, string>>({});

  // The grid's tile set is the cohort-derived base spec (the Address
  // Verification OPTIONAL→REQUIRED mismatch flip moved with the relocated
  // tile — see RELOCATED_TILE_IDS note above).
  const tileSpecs = baseTileSpecs;

  // Person-Modal consolidation (Bill 2026-06-11) — HYDRATED captures render
  // as captured-state tiles (D4: ✓ + filename + re-scan affordance). For an
  // existing-CIF edit / review-add, the body hydrates prior captures onto
  // `person.extra.attachedDocs` (Slot-4 T5 doc-library fetch, async) — this
  // effect maps each attached doc onto its tile by canonical docCode and
  // ADDS captured state (never removes; deletes stay user-driven). Two
  // passes: specific tiles claim their codes first, then the generic
  // primary-id / secondary-id slots claim from the photo-ID family.
  useEffect(() => {
    const docs = readAttachedDocs(person);
    if (docs.length === 0) return;
    const byCode = new Map(docs.map((d) => [d.docCode, d] as const));
    const claimed = new Set<string>();
    const mapped: Array<{ tileId: string; doc: AttachedDoc }> = [];
    // Pass 1 — specific tiles (foreign-passport → PASSPORT, i94 → I94, …).
    for (const t of tileSpecs) {
      if (t.tileId === 'primary-id' || t.tileId === 'secondary-id') continue;
      const code = tileIdToCanonicalDocCode(t.tileId);
      const doc = byCode.get(code);
      if (doc && !claimed.has(code)) {
        claimed.add(code);
        mapped.push({ tileId: t.tileId, doc });
      }
    }
    // Pass 2 — generic photo-ID slots adopt whatever family member is on file.
    const PHOTO_ID_FAMILY = ['GOVT_ID', 'PASSPORT', 'GREEN_CARD', 'SSN_CARD', 'BIRTH_CERT'];
    for (const t of tileSpecs) {
      if (t.tileId === 'primary-id') {
        const code = PHOTO_ID_FAMILY.find((c) => byCode.has(c) && !claimed.has(c));
        if (code) {
          claimed.add(code);
          mapped.push({ tileId: t.tileId, doc: byCode.get(code)! });
        }
      } else if (t.tileId === 'secondary-id') {
        const code = PHOTO_ID_FAMILY.map((c) => `${c}_2`).find((c) => byCode.has(c) && !claimed.has(c));
        if (code) {
          claimed.add(code);
          mapped.push({ tileId: t.tileId, doc: byCode.get(code)! });
        }
      }
    }
    if (mapped.length === 0) return;
    // Additive merge — skip when every mapped tile is already captured.
    setCapturedTileIds((prev) => {
      const missing = mapped.filter((m) => !prev.has(m.tileId));
      if (missing.length === 0) return prev;
      const next = new Set(prev);
      for (const m of missing) next.add(m.tileId);
      // Defer the parent notification out of the render-phase updater.
      setTimeout(() => notifyCaptureState(next), 0);
      return next;
    });
    setAttachedByTileId((prev) => {
      const additions = mapped.filter((m) => !prev[m.tileId]);
      if (additions.length === 0) return prev;
      const next = { ...prev };
      for (const m of additions) next[m.tileId] = m.doc;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person, tileSpecs]);

  // Step 5 — CIP slot-machine reassignment. Re-runs every render after a
  // capture so a later DL bumps an earlier Passport from Primary to
  // Secondary (DL F254=1 < Passport F254=3). Drives the PRIMARY ID /
  // SECONDARY ID pill on tiles + the persisted `extra.idType` / `idType2`
  // values (Core Director F254 / F814 — both rolling-window codes).
  const slotCaptures = useMemo<SlotCapture[]>(() => {
    const extractsRoot = ((person.extra as Record<string, unknown> | undefined)?.extracts ?? {}) as Record<string, unknown>;
    // 0600 — thread the tileId as the legacy extracts key (pre-canonical
    // azure-di-id captures keyed extracts by the route value = tile id).
    return Object.entries(capturedSlots)
      .filter(([, info]) => info.branding.idCodeF254 != null)
      .map(([tileId, info]) => ({
        tileId,
        idCode: String(info.branding.idCodeF254),
        docTypeValue: info.docTypeValue,
        family: pullCipFamily(cipExtractsFor(extractsRoot, info.docTypeValue, tileId)),
      }));
  }, [capturedSlots, person.extra]);

  // Step 5 persist — push the slot plan into person.extra so downstream CIP
  // tabs + the JHA CustAdd payload pick up the F254 / F814 codes for whichever
  // doc landed in Primary / Secondary. Each capture's family comes from its
  // stored extract (with the Apply-step corrections folded in, CHG-0134); IDs
  // already on the person rank as incumbents (CHG-0135). Runs only when the
  // scanned IDs or their values change (the signature ref), so an officer's
  // own edit to an ID field is never fought over.
  const lastPersistedRanking = useRef<string>('');
  useEffect(() => {
    const signature = JSON.stringify(slotCaptures);
    if (signature === lastPersistedRanking.current) return;
    lastPersistedRanking.current = signature;

    const extra = (person.extra ?? {}) as Record<string, unknown>;
    const s = (k: string): string => (typeof extra[k] === 'string' ? (extra[k] as string) : '');
    const onFile = (suffix: '' | '2'): OnFileId | undefined => (s(`idType${suffix}`)
      ? {
          idType: s(`idType${suffix}`),
          family: {
            idNumber: s(`idNumber${suffix}`), idIssuer: s(`idIssuer${suffix}`),
            idIssueDate: s(`idIssueDate${suffix}`), idExpiryDate: s(`idExpiryDate${suffix}`),
          },
        }
      : undefined);
    const primary = onFile('');
    const secondary = onFile('2');
    const plan = planCipSlots(slotCaptures, { ...(primary ? { primary } : {}), ...(secondary ? { secondary } : {}) }, cipIdPriorityRules);
    if (Object.keys(plan.extraPatch).length > 0) {
      onChange({ extra: plan.extraPatch });
    }
    try {
      const slotOf = (source?: string) => (source && capturedSlots[source]
        ? { docType: capturedSlots[source].docTypeValue, f254: capturedSlots[source].branding.idCodeF254 }
        : source ?? null);
      // eslint-disable-next-line no-console
      console.info('[audit]', 'pdw.stage4.cip-slot-machine', {
        cohort,
        primary: slotOf(plan.primary),
        secondary: slotOf(plan.secondary),
      });
    } catch {
      /* swallow */
    }
  }, [slotCaptures, cipIdPriorityRules, capturedSlots, onChange, cohort, person.extra]);

  // Build the DocsToCaptureGrid tile-state per-render so captured-state
  // changes immediately reflect in the grid. v0.14.52 (BACKLOG-0074) —
  // include `extractedFields` + `fieldsRead` on captured tiles so the
  // Stage 8 quick-preview drawer renders the canonical SPEC Screen 16
  // body (field-name/value/confidence list).
  //
  // Step 2 (Bill 2026-05-28) — generic primary-id / secondary-id tiles
  // adopt the classified doc-type's label + icon via `capturedAs`.
  const tiles: ReadonlyArray<DocTileSpec> = useMemo(() => {
    return tileSpecs.map((t) => {
      const captured = capturedTileIds.has(t.tileId);
      const extracted = captured ? extractedByTileId[t.tileId] : undefined;
      const slotInfo = captured ? capturedSlots[t.tileId] : undefined;
      const thumb = captured ? thumbnailByTileId[t.tileId] : undefined;
      const attached = captured ? attachedByTileId[t.tileId] : undefined;
      const spec: DocTileSpec = {
        tileId: t.tileId,
        label: t.label,
        hint: t.hint,
        iconSlug: t.iconSlug,
        gatingLevel: t.gatingLevel,
        status: captured ? 'captured' : 'idle',
        ...(extracted && extracted.length > 0
          ? { extractedFields: extracted, fieldsRead: extracted.length }
          : {}),
        ...(slotInfo
          ? {
              capturedAs: {
                docTypeValue: slotInfo.docTypeValue,
                label: slotInfo.branding.label,
                iconSlug: slotInfo.branding.iconSlug,
                ...(slotInfo.branding.idCodeF254
                  ? { idCodeF254: slotInfo.branding.idCodeF254 }
                  : {}),
              },
            }
          : {}),
        // GAP E (Bill 2026-05-28) — drawer thumbnail. Prefer the pdf.js
        // first-page render (image/jpeg) when available; fall back to the
        // AttachedDoc.imageDataUrl when the source was a plain image.
        ...(thumb
          ? { thumbnailDataUrl: thumb, thumbnailMimeType: 'image/jpeg' as const }
          : attached && attached.imageDataUrl
            ? { thumbnailDataUrl: attached.imageDataUrl, thumbnailMimeType: attached.contentType }
            : {}),
        ...(attached?.filename ? { filename: attached.filename } : {}),
      };
      return spec;
    });
  }, [tileSpecs, capturedTileIds, extractedByTileId, capturedSlots, thumbnailByTileId, attachedByTileId]);

  // Required tile ids come from the DOWNGRADED specs (baseTileSpecs) so the
  // existing-customer Primary-Photo-ID downgrade actually releases the gate.
  // Re-deriving from cohortDocTiles(cohort) would ignore it. Fall back to the
  // cohort helper when the downgrade left the specs untouched (fresh add).
  const requiredTileIds = useMemo(
    () => new Set(baseTileSpecs.filter((t) => t.gatingLevel === 'REQUIRED').map((t) => t.tileId)),
    [baseTileSpecs],
  );

  const notifyCaptureState = useCallback(
    (nextCaptured: ReadonlySet<string>): void => {
      onCaptureStateChange({
        capturedTileIds: nextCaptured,
        allRequiredCaptured: [...requiredTileIds].every((id) => nextCaptured.has(id)),
      });
    },
    [requiredTileIds, onCaptureStateChange],
  );

  const handleTileClick = useCallback(
    (tileId: string): void => {
      const spec = tileSpecs.find((t) => t.tileId === tileId);
      if (!spec) return;
      // BACKLOG-0074 — captured tile → quick-preview drawer (toggle);
      // idle / failed tile → scan modal.
      if (capturedTileIds.has(tileId)) {
        setPreviewTileId((prev) => (prev === tileId ? null : tileId));
        return;
      }
      if (readOnly) return; // view-only — no fresh captures
      setPreviewTileId(null);
      setDroppedFile(null);
      setScanTile(spec);
    },
    [tileSpecs, capturedTileIds, readOnly],
  );

  /**
   * slot-1 G4/T6 — drag-drop a file directly onto a tile. Previously a silent
   * no-op (no consumer passed `onTileDrop`). Now: select the tile, hand the
   * dropped file to PerDocScanModal as `initialFile` so it preflights +
   * auto-starts extraction immediately (no start gate, D1), flowing through to
   * the Apply step (kept). End-to-end wired — proven by the modal opening with
   * the dropped bytes already extracting.
   */
  const handleTileDrop = useCallback(
    (tileId: string, file: File): void => {
      if (readOnly) return;
      const spec = tileSpecs.find((t) => t.tileId === tileId);
      if (!spec) return;
      setPreviewTileId(null);
      setDroppedFile(file);
      setScanTile(spec);
    },
    [tileSpecs, readOnly],
  );

  /**
   * PerDocScanModal `onCapture` handler — dispatches to the server-side
   * scan endpoint. Today every cohort routes through v3.nra.scan; the
   * server's classifier handles known doc types (passport / visa / i94 /
   * i20 / ds2019 / green-card / ead / ssn) and gracefully no-ops for
   * extension slot codes (us-drivers-license, address-proof — kept for
   * future Azure DI model registration). The raw file is captured either
   * way; OCR extraction only fires for known slots.
   */
  // Capture-surface unification (Bill 2026-05-27, mockup v15-approved).
  // Per-tile capture now routes through the SAME engine the auto-classify
  // surface uses: makeScanHandler → routeCapture → azure-di-id branch
  // (IdExtractionService — the proven SPFx pipeline: splitFirstMiddle ·
  // parseAddress · postProcessAddress · USPS · titleCase · aiOriginals) for
  // photo-IDs OR the v3-nra branch (apiClient.retail.nra.scan +
  // buildPatchFromNra) for foreign / NRA docs. The patch is applied at
  // scan time via applyPatch=onChange (consistent with the auto-classify
  // + Individual review surfaces); handleApply persists the AttachedDoc +
  // marks captured.
  // Cart linkage (Bill 2026-10-16): ID scans landed in dbo.DocumentExtract with a
  // blank CartId because this host never passed the active cart.
  const activeCartId = useOptionalCart()?.cart?.cartId;
  const scanHandler = useMemo(
    () => makeScanHandler({ apiClient, person, applyPatch: onChange, ...(activeCartId ? { cartId: activeCartId } : {}) }),
    [apiClient, person, onChange, activeCartId],
  );
  // Step 4 — autoclassify so CaptureModal shows "Detected as …" + the F254
  // override picker pre-selects the detected type. Salvaged 2-pass classifier
  // (Azure DI prebuilt-idDocument → /v3/nra/classify) bound to the api client.
  const autoClassify = useMemo(
    () => makeAutoClassify(apiClient, activeCartId ? { cartId: activeCartId } : undefined),
    [apiClient, activeCartId],
  );
  // Step 4 — Classify Picker options (F254 1-8, sorted ascending). Shown in
  // CaptureModal's override dropdown so the FSR can correct a mis-classify
  // and see the JHA Core Director field mapping (F254 IdVerifyCode) inline.
  // Only surfaced for the generic Primary / Secondary ID slots — specific
  // tiles (passport, green-card, i94, etc.) already pin the doc type.
  const classifyOptions = useMemo(() => classifyPickerOptions(), []);
  const overrideOptions = useMemo(
    () => classifyOptions.map((o) => ({ value: o.value, label: o.label })),
    [classifyOptions],
  );
  const isGenericSlot = scanTile?.tileId === 'primary-id' || scanTile?.tileId === 'secondary-id';


  const handleCapture = useCallback(
    async (base64: string, mimeType: string, chosenDocType?: string): Promise<ScanResultPayload> => {
      if (!scanTile) return { success: false, error: 'No tile selected' };
      // slot-1 T4 — manual tiles (H-1B I-797, visa-class proof) have no trained
      // extractor: attach only, no Azure DI call. (Handled internally by
      // routeCapture for mode='no-extraction', but short-circuit here too for
      // clarity / a tile spec carrying noExtraction without a DOC_TYPE_OPTIONS row.)
      if (scanTile.noExtraction) {
        return { success: true, fields: {}, needsConfirm: [] };
      }
      // Step 4 — when CaptureModal returns a `chosenDocType` (FSR overrode
      // the auto-classified suggestion in the F254 picker), route through
      // THAT doc type's mapper instead of the tile's static id.
      //
      // Bill 2026-05-28 GAP A follow-up: an empty 'other' chosenDocType
      // (e.g. autoClassify couldn't recognize) MUST NOT override a specific
      // tile id — e.g. the SSN tile must extract as 'ssn-card' even when
      // autoClassify returned 'other'. Only honor chosenDocType when it
      // names a real DOC_TYPE_OPTIONS value.
      const routeKey =
        chosenDocType && chosenDocType !== 'other'
          ? chosenDocType
          : scanTile.tileId;
      return scanHandler(routeKey, base64, mimeType);
    },
    [scanHandler, scanTile],
  );

  /**
   * On Apply, promote OCR'd field values to canonical RaoPerson slots
   * (via mapNraScanToPersonPatch — same logic the legacy in-Stage-9
   * Documents tab uses), mark the tile captured, then auto-advance to
   * the next required tile if one remains. v0.14.51 BACKLOG-0073.
   */
  const handleApply = useCallback(
    (result: ScanResultPayload, artifact: CaptureArtifact, chosenDocType?: string): void => {
      if (!scanTile || !result.success) return;
      const reviewing = reviewTileId === scanTile.tileId;
      setLastScans((prev) => ({ ...prev, [scanTile.tileId]: { result, artifact, ...(chosenDocType ? { chosenDocType } : {}) } }));
      // Consume the dropped file (if any) so the next/auto-advanced tile opens
      // to the chooser rather than re-using the previous tile's bytes.
      setDroppedFile(null);
      // NRA smoke test (Bill 2026-06-12) — Apply-step corrections. Values the
      // FSR fixed inline in the capture modal (and document-side conflict
      // picks) win over the raw extract. The host's onChange is the
      // extra-aware applier (deep-merges `extra` one level), matching the
      // CIP slot-machine effect's only-new-keys extra patches.
      // (Applied below, once the document type is resolved.)
      // Capture-unification (Bill 2026-05-27) — the person patch is applied
      // at scan time via makeScanHandler → routeCapture (applyPatch = onChange).
      // handleApply persists the AttachedDoc + marks the tile captured.
      // result.raw is now scanRouter's pass-through { detectedRawType,
      // mismatchWarning } for audit / mis-routing surfacing.
      const rawInfo = result.raw as { detectedRawType?: string; mismatchWarning?: string } | undefined;

      // GAP B fix (Bill 2026-05-28) — write the CANONICAL Stage-9 DocCode
      // onto the AttachedDoc so the Stage-9 Documents tab + Verify tab
      // recognize Stage-4 captures as fulfilling their catalog tiles.
      // Generic primary-id/secondary-id slots translate via the classified
      // doc-type (us-drivers-license → GOVT_ID, us-passport → PASSPORT,
      // green-card → GREEN_CARD); specific tiles map directly (ssn-card →
      // SSN_CARD, passport → PASSPORT, etc.). Keyed locally by tileId so a
      // re-capture replaces the prior slot.
      //
      // BEFORE THE FIX: docCode = scanTile.tileId ("primary-id") meant Stage 9
      // looked for GOVT_ID and got nothing → "0/1 required" even though
      // Stage 4 captured. AFTER: docCode = "GOVT_ID" → Stage 9 recognizes it.
      const detectedVForCode =
        chosenDocType && chosenDocType !== 'other'
          ? chosenDocType
          : rawInfo?.detectedRawType
            ? azureDiTypeToDocTypeValue(rawInfo.detectedRawType)
            : undefined;
      const canonicalDocCode = tileIdToCanonicalDocCode(scanTile.tileId, detectedVForCode);
      const attachedDoc: AttachedDoc = {
        docCode: canonicalDocCode,
        source: 'cip-scan',
        filename: artifact.originalFilename ?? `${scanTile.tileId}-${Date.now()}.pdf`,
        contentType: artifact.pdfMimeType,
        sizeBytes: estimateBase64Size(artifact.pdfBase64),
        imageDataUrl: '',
        fileUrl: '',
        capturedAt: new Date().toISOString(),
      };
      setAttachedByTileId((prev) => {
        const next = { ...prev, [scanTile.tileId]: attachedDoc };
        onAttachedDocsChange?.(Object.values(next));
        return next;
      });

      // GAP E — render the first-page JPEG from the PDF artifact via pdf.js
      // so the drawer's <img> can show the captured doc instead of the
      // "Thumbnail unavailable" placeholder. Fire-and-forget; the drawer
      // re-renders when state lands.
      pdfFirstPageThumbnailFromBase64(artifact.pdfBase64, { width: 240 })
        .then((dataUrl) => {
          if (dataUrl) setThumbnailByTileId((prev) => ({ ...prev, [scanTile.tileId]: dataUrl }));
        })
        .catch(() => { /* swallow — thumbnail is best-effort */ });

      // Audit — emit per SPEC § 7.4.2. Bus integration is TBD; for now
      // console.info is the canonical interim per audit-event roadmap.
      // (Mirrors the emitNraPathEvent pattern in IndividualNraDocumentScanTabs.)
      try {
        // eslint-disable-next-line no-console
        console.info('[audit]', 'pdw.stage4.tile.captured', {
          tileId: scanTile.tileId,
          cohort,
          // modelId/pageCount no longer threaded post-unification; PersonDocumentScanAudit
          // (BE) captures these server-side. detectedRawType + mis-route warning surfaced here.
          ...(rawInfo?.detectedRawType ? { detectedRawType: rawInfo.detectedRawType } : {}),
          ...(rawInfo?.mismatchWarning ? { mismatchWarning: rawInfo.mismatchWarning } : {}),
        });
      } catch {
        /* swallow — telemetry must never break capture flow */
      }

      // Mark tile captured + auto-advance to next required-uncaptured tile.
      const next = new Set(capturedTileIds);
      next.add(scanTile.tileId);
      setCapturedTileIds(next);
      notifyCaptureState(next);

      // BACKLOG-0074 — cache the extracted-field snapshot so the Stage 8
      // quick-preview drawer can surface it without a refetch. NRA smoke
      // test (Bill 2026-06-12): run the same plain-language dedupe the
      // capture modal uses (one row per logical fact, no backend keys, MRZ
      // excluded as a peer row) so the drawer matches the modal.
      const extracted: ExtractedFieldRow[] = result.fields
        ? buildDisplayRows(result.fields)
            .filter((r) => !r.secondary)
            .map((r) => ({ key: r.label, value: r.displayValue, confidence: r.confidence }))
        : [];
      setExtractedByTileId((prev) => ({ ...prev, [scanTile.tileId]: extracted }));

      // Step 2 (Bill 2026-05-28) — adopt-on-classify. Priority order for the
      // adopted branding:
      //   1. `chosenDocType` (FSR overrode via the F254 Classify Picker)
      //   2. auto-detected raw type (Azure DI prebuilt-idDocument result)
      //   3. tile's own docTypeValue (specific tiles like passport / green-card)
      // Generic primary-id / secondary-id slots adopt #1 or #2; specific
      // tiles fall through to #3 and ALSO record their F254 so the CIP
      // slot-machine can rank them.
      //
      // 2026-06-12 (tile-render divergence investigation) — the old
      // `?? docTypeBranding('other')` terminal fallback is REMOVED and
      // noExtraction tiles are excluded entirely. cohortDocTiles is the
      // SINGLE SOURCE for tile label/hint/gating; this adoption step is the
      // only sanctioned post-capture override and it previously violated
      // that contract: manual tiles with no DOC_TYPE_BRANDING row (i797,
      // visa-class-proof) fell through to the 'other' branding, which
      // (a) rebranded the captured tile "Other Document" (label + tax-form
      // icon substituted for the spec's), and (b) injected F254 code '8'
      // into the CIP slot machine for a NON-identity document — an I-797
      // could claim CIP ID-1/ID-2 and write extra.idType(2)='8'. Now: no
      // branding row → no adoption → the tile keeps its cohortDocTiles spec
      // and never enters the CIP ranking.
      const detectedValue =
        rawInfo?.detectedRawType ? azureDiTypeToDocTypeValue(rawInfo.detectedRawType) : 'other';
      const resolvedValue =
        chosenDocType && chosenDocType !== 'other'
          ? chosenDocType
          : detectedValue !== 'other'
            ? detectedValue
            : scanTile.tileId;
      const adopt = scanTile.noExtraction
        ? undefined
        : docTypeBranding(resolvedValue) ?? docTypeBranding(scanTile.tileId);
      if (adopt) {
        setCapturedSlots((prev) => ({
          ...prev,
          [scanTile.tileId]: {
            docTypeValue: resolvedValue,
            branding: adopt,
          },
        }));
      }

      // Apply-step corrections (NRA smoke test, Bill 2026-06-12): values the FSR fixed inline win over the raw
      // extract. CHG-0134 (MacKenzie 10/29): for an ID in the CIP ranking, the ID's own corrections also go into
      // its stored extract and NOT straight to slot 1. The slot plan re-ranks on Apply and refilled slot 1 from
      // the raw extract, dropping the corrections; and a correction on a second ID belongs in slot 2.
      if (result.corrections && Object.keys(result.corrections).length > 0) {
        const ranked = adopt?.idCodeF254 != null;
        const direct: Record<string, string> = {};
        for (const [k, v] of Object.entries(result.corrections)) {
          if (!(ranked && CIP_CORRECTION_KEYS.has(k))) direct[k] = v;
        }
        const correctionPatch = correctionsToPersonPatch(direct, { addr: person.addr });
        if (ranked) {
          const extractsRoot = ((person.extra as Record<string, unknown> | undefined)?.extracts ?? {}) as Record<string, unknown>;
          const key = extractsKeyForDocType(extractsRoot, resolvedValue, [scanTile.tileId]) ?? resolvedValue;
          const stored = (extractsRoot[key] ?? {}) as Record<string, unknown>;
          correctionPatch.extra = {
            ...(correctionPatch.extra ?? {}),
            extracts: { [key]: extractWithCorrections(stored, result.corrections) },
          } as RaoPerson['extra'];
        }
        if (Object.keys(correctionPatch).length > 0) onChange(correctionPatch);
      }

      // Auto-advance: find the next REQUIRED tile that's not yet captured.
      // If found, leave PerDocScanModal open + swap the tile spec; if not,
      // close the modal so the FSR sees the Continue affordance.
      const nextRequired = tileSpecs.find(
        (t) => t.gatingLevel === 'REQUIRED' && !next.has(t.tileId),
      );
      if (reviewing) {
        setReviewTileId(null);
        setScanTile(null);
      } else if (nextRequired) {
        setScanTile(nextRequired);
      } else {
        setScanTile(null);
      }
    },
    [scanTile, cohort, person, onChange, capturedTileIds, notifyCaptureState, tileSpecs, onAttachedDocsChange, reviewTileId],
  );

  // Review import — reopen the tile's last capture on its review step.
  const handlePreviewReview = useCallback(
    (tileId: string): void => {
      const spec = tileSpecs.find((t) => t.tileId === tileId);
      if (!spec || !lastScans[tileId]) return;
      setPreviewTileId(null);
      setReviewTileId(tileId);
      setScanTile(spec);
    },
    [tileSpecs, lastScans],
  );
  const reviewableTileIds = useMemo(() => new Set(Object.keys(lastScans)), [lastScans]);

  // BACKLOG-0074 — Quick-preview drawer action handlers.
  const handlePreviewRescan = useCallback(
    (tileId: string): void => {
      const spec = tileSpecs.find((t) => t.tileId === tileId);
      if (!spec) return;
      // Close drawer + open the scan modal in replace mode. PerDocScanModal
      // doesn't yet plumb a `replace` flag — the new scan overwrites the
      // patched person fields via mapNraScanToPersonPatch (FSR-explicit
      // overwrite is the desired behaviour per SPEC § "Locked behaviors").
      setPreviewTileId(null);
      setScanTile(spec);
    },
    [tileSpecs],
  );

  const handlePreviewDelete = useCallback(
    async (tileId: string): Promise<void> => {
      const spec = tileSpecs.find((t) => t.tileId === tileId);
      if (!spec) return;
      // BACKLOG-1297 — in-app confirm; window.confirm() never paints in Teams,
      // so the capture used to disappear (or not) with no question asked.
      const ok = await confirm({
        title: 'Remove this capture?',
        message: `The extracted values from the ${spec.label} clear from this person. `
          + 'The scan audit row is retained per 7-yr retention policy.',
        confirmLabel: 'Remove capture',
        cancelLabel: 'Keep it',
        tone: 'danger',
      });
      if (!ok) return;
      // Drop from capturedTileIds + cache. The person-form draft fields
      // are not cleared here (Bill's existing UX precedent — IndividualNraDocumentScanTabs
      // uses `clearFieldsForDoc` to null specific slots; doing the same
      // for the Stage 4 surface lands in a follow-up so the audit trail
      // for the clear action gets emitted at the right grain).
      setCapturedTileIds((prev) => {
        const next = new Set(prev);
        next.delete(tileId);
        notifyCaptureState(next);
        return next;
      });
      setExtractedByTileId((prev) => {
        const next = { ...prev };
        delete next[tileId];
        return next;
      });
      // Step 2 — clear the adopt-on-classify slot info so the generic
      // primary-id / secondary-id tile reverts to its base label/icon
      // after delete (and so the CIP slot-machine re-ranks correctly).
      setCapturedSlots((prev) => {
        const next = { ...prev };
        delete next[tileId];
        return next;
      });
      // GAP E — clear the rendered thumbnail too.
      setThumbnailByTileId((prev) => {
        const next = { ...prev };
        delete next[tileId];
        return next;
      });
      // C2 — drop the attached doc + re-emit the running set so PdwShell
      // removes it from the persisted draft.
      setAttachedByTileId((prev) => {
        const next = { ...prev };
        delete next[tileId];
        onAttachedDocsChange?.(Object.values(next));
        return next;
      });
      setPreviewTileId(null);
      try {
        // eslint-disable-next-line no-console
        console.info('[audit]', 'pdw.stage4.tile.deleted', { tileId, cohort });
      } catch {
        /* swallow */
      }
    },
    [tileSpecs, cohort, notifyCaptureState, onAttachedDocsChange, confirm],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <DocsToCaptureGrid
        tiles={tiles}
        onTileClick={handleTileClick}
        onTileDrop={handleTileDrop}
        previewTileId={previewTileId}
        onPreviewClose={() => setPreviewTileId(null)}
        {...(!readOnly ? { onPreviewRescan: handlePreviewRescan, onPreviewDelete: handlePreviewDelete, onPreviewReview: handlePreviewReview, reviewableTileIds } : {})}
      />

      {!readOnly && scanTile && (
        <PerDocScanModal
          open={scanTile !== null}
          docLabel={scanTile.label}
          docHint={scanTile.hint}
          iconSlug={scanTile.iconSlug}
          initialFile={droppedFile}
          onCapture={handleCapture}
          onApply={handleApply}
          onClose={() => { setScanTile(null); setDroppedFile(null); setReviewTileId(null); }}
          {...(reviewTileId && scanTile.tileId === reviewTileId && lastScans[reviewTileId] ? { reviewOf: lastScans[reviewTileId] } : {})}
          // Attach-only tiles (e.g. "Other supporting document") skip the
          // scan-time screening — the modal states the military-ID
          // restriction (18 USC 701) in plain language instead.
          attachOnly={!!scanTile.noExtraction || findDocType(scanTile.tileId)?.mode === 'no-extraction'}
          // Step 4 — generic primary-id / secondary-id slots get the F254
          // Classify Picker (1-8) + auto-classifier so the FSR sees the
          // detected type and can correct it. Specific tiles (passport,
          // green-card, i94, i20, ds2019, ssn, address-proof) pin the doc
          // type via the tile itself; no override surface needed.
          {...(isGenericSlot ? { overrideOptions, autoClassify } : {})}
        />
      )}
    </div>
  );
}
